import { PRODUCT_GROUP_FRAGMENT } from 'data/fragments/productGroup';
import gql from 'graphql-tag';

import { CATALOG_PRODUCT_FRAGMENT, PRODUCT_FRAGMENT } from '../fragments/product';
import { COLUMN_PRODUCT_FRAGMENT, COLUMN_PRODUCT_LIGHT_FRAGMENT } from '@/data/fragments/columnProduct';
import { SITE_PRODUCT_LIGHT_FRAGMENT } from '@/data/fragments/siteProduct';

export const GET_TRANSFERABLE_PRODUCTS_QUERY = gql`
    query GET_TRANSFERABLE_PRODUCTS($originColumnId: ID!, $destinationColumnId: ID!, $transferDate: String) {
        transferableProducts(
            originColumnId: $originColumnId
            destinationColumnId: $destinationColumnId
            transferDate: $transferDate
        ) {
            productId
            fullname
            conditionning
            packagesPerBatch
            originStockAvailable
            destinationStockAvailable
            quantity
        }
    }
`;

export const GET_TRANSFERABLE_PRODUCTS_FROM_CSV_QUERY = gql`
    query GET_TRANSFERABLE_PRODUCTS_FROM_CSV(
        $originColumnId: ID!
        $destinationColumnId: ID!
        $csvData: [TransferCsvData]!
        $isDestinationQuantity: Boolean!
    ) {
        transferableProductsFromCsv(
            originColumnId: $originColumnId
            destinationColumnId: $destinationColumnId
            csvData: $csvData
            isDestinationQuantity: $isDestinationQuantity
        ) {
            transferableProducts {
                productId
                fullname
                conditionning
                packagesPerBatch
                originStockAvailable
                destinationStockAvailable
                quantity
            }
            idsNotFound
            idsArchived
        }
    }
`;

export const GET_PRODUCTS_WITH_STOCK_BY_COLUMNS_QUERY = gql`
    query GET_PRODUCTS_WITH_STOCK_BY_COLUMNS($columnIds: [ID!]!) {
        productsWithStockByColumns(columnIds: $columnIds) {
            _id
            brand
            categoryId
            expiryDateRecordsWarehouse {
                _id
                expiryDate
                initialQuantity
                quantityAtInventory
            }
            flow
            name
            stockByColumns(columnIds: $columnIds) {
                columnId
                stock
                stockAvailable
            }
            trackExpiryDate
            volume
            isDoubleCoil
            isWithoutStock
        }
    }
`;

export const GET_CATALOG_PRODUCTS_QUERY = gql`
    query GET_CATALOG_PRODUCTS(
        $offset: Int
        $limit: Int
        $sortBy: String
        $sortOrder: String
        $filters: CatalogProductsFilterInput
    ) {
        catalogProducts(offset: $offset, limit: $limit, sortBy: $sortBy, sortOrder: $sortOrder, filters: $filters) {
            hasMore
            products {
                ...CatalogProductFragment
            }
        }
    }
    ${CATALOG_PRODUCT_FRAGMENT}
`;

export const GET_DETAILED_PRODUCT_QUERY = gql`
    query GET_DETAILED_PRODUCT($productId: ID!) {
        detailedProduct(productId: $productId) {
            _id
            comment
            ...ProductFragment
            isOutOfStock
            locations {
                _id
                datamatrix
                locationType
                warehouseColumnId
                warehouseColumn {
                    name
                }
            }
            productGroup {
                ...ProductGroupFragment
            }
            productGroupsUsedIn {
                ...ProductGroupFragment
            }
            shouldOrderOnce
        }
    }
    ${PRODUCT_FRAGMENT}
    ${PRODUCT_GROUP_FRAGMENT}
`;

export const GET_NON_ARCHIVED_PRODUCTS_QUERY = gql`
    query GET_NON_ARCHIVED_PRODUCTS {
        nonArchivedProducts {
            _id
            name
            brand
            volume
        }
    }
`;

export const GET_ALL_PRODUCTS_QUERY = gql`
    query GET_ALL_PRODUCTS {
        getAllProducts {
            _id
            brand
            categoryId
            fullname
            height
            imageUrl
            isDoubleCoil
            isMicrostore
            shouldBeHiddenInStore
            isToBeArchived
            isWithoutStock
            length
            name
            state
            volume
            weight
            width
        }
    }
`;

export const GET_AVAILABLE_FOR_MICROSTORE_QUERY = gql`
    query GET_AVAILABLE_FOR_MICROSTORE {
        availableForMicrostore {
            _id
            brand
            fullname
            height
            imageUrl
            isMicrostore
            shouldBeHiddenInStore
            length
            name
            state
            volume
            weight
            width
        }
    }
`;

export const GET_OFF_DATA_QUERY = gql`
    query GET_OFF_DATA($barcodes: [String!]!) {
        OFFProductData(barcodes: $barcodes) {
            success
            error
            productNutritionData {
                allergens
                ingredients
                nutrition {
                    carbohydrates
                    energyKcal
                    fat
                    fiber
                    proteins
                    saturatedFat
                    sodium
                    sugars
                }
                nutriscore
                traces
            }
        }
    }
`;

export const GET_PRODUCTS_BY_CATEGORY_QUERY = gql`
    query GET_PRODUCTS_BY_CATEGORY($categoryId: ID!) {
        getProductsByCategory(categoryId: $categoryId) {
            _id
            availableStock
            fullname
            portion
        }
    }
`;

export const GET_TAG_PRODUCTS_QUERY = gql`
    query GET_TAG_PRODUCTS($tagId: ID!) {
        productsWithTagId(tagId: $tagId) {
            _id
            fullname
        }
    }
`;

export const GET_COLUMN_PRODUCTS_QUERY = gql`
    query GET_COLUMN_PRODUCTS(
        $columnId: ID!
        $offset: Int
        $limit: Int
        $sortBy: String
        $sortOrder: String
        $filters: ColumnProductsFilterInput
    ) {
        columnProducts(
            columnId: $columnId
            offset: $offset
            limit: $limit
            sortBy: $sortBy
            sortOrder: $sortOrder
            filters: $filters
        ) {
            hasMore
            columnProducts {
                ...ColumnProductLight
            }
        }
    }
    ${COLUMN_PRODUCT_LIGHT_FRAGMENT}
`;

export const GET_SITE_PRODUCTS_QUERY = gql`
    query GET_SITE_PRODUCTS(
        $siteId: ID!
        $offset: Int
        $limit: Int
        $sortBy: String
        $sortOrder: String
        $filters: SiteProductsFilterInput
    ) {
        siteProducts(
            siteId: $siteId
            offset: $offset
            limit: $limit
            sortBy: $sortBy
            sortOrder: $sortOrder
            filters: $filters
        ) {
            hasMore
            siteProducts {
                ...SiteProductLight
            }
        }
    }
    ${SITE_PRODUCT_LIGHT_FRAGMENT}
`;

export const GET_COLUMN_PRODUCT_QUERY = gql`
    query GET_COLUMN_PRODUCT($columnId: ID!, $productId: ID!) {
        columnProduct(columnId: $columnId, productId: $productId) {
            ...ColumnProduct
        }
    }
    ${COLUMN_PRODUCT_FRAGMENT}
`;

export const GET_COLUMNS_PRODUCT_QUERY = gql`
    query GET_COLUMNS_PRODUCT($productId: ID!) {
        columnsProduct(productId: $productId) {
            columnId
            stock
            column {
                _id
                fullname
                type
            }
        }
    }
`;

export const GET_MICROSTORE_PRODUCTS_QUERY = gql`
    query GET_MICROSTORE_PRODUCTS {
        getMicrostoreProducts {
            _id
            brand
            flow
            imageUrl
            isDoubleCoil
            name
            trackExpiryDate
            volume
        }
    }
`;

export const GET_PRODUCT_PREPARATION_DELAY_QUERY = gql`
    query GET_PRODUCT_PREPARATION_DELAY($productId: ID!) {
        productPreparationDelay(productId: $productId)
    }
`;

export const GET_PRODUCTS_BY_ID_QUERY = gql`
    query GET_PRODUCTS_BY_ID($productIds: [ID!]!) {
        getProductsById(productIds: $productIds) {
            _id
            fullname
        }
    }
`;

export const GET_SUPPLIER_PRODUCTS_QUERY = gql`
    query GET_SUPPLIER_PRODUCTS($supplierId: ID!) {
        getSupplierProducts(supplierId: $supplierId) {
            _id
            fullname
        }
    }
`;

export const GET_PRODUCTS_WITH_RECIPES_QUERY = gql`
    query GET_PRODUCTS_WITH_RECIPES {
        getAllProducts {
            _id
            categoryId
            fullname
            imageUrl
            state
            weightWithoutPackaging
            recipe {
                ingredients {
                    productId
                    quantity
                    hotDrinksIngredientUnit
                    product {
                        _id
                        categoryId
                        fullname
                        imageUrl
                        weightWithoutPackaging
                    }
                }
            }
        }
    }
`;
